import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bookmark, Eraser, Focus, Hand, LocateFixed, Minus, MousePointer2, PaintBucket, Palette, Pencil, Pipette, Plus, Redo2, RotateCcw, Ruler, ScanLine, Settings2, Undo2, ZoomIn, ZoomOut } from 'lucide-react';
import { MARD_291, hexToRgb, readableTextColor } from '../core/palette';
import { watermarkCells } from '../core/watermark';
import { findColorRuns } from '../core/colorRuns';
import type { CanvasSelection, CanvasTool, FocusMode, GridMode, MouseButtonMap, PatternProject, PatternWatermark, RunAssistMode } from '../types';

interface Props {
  project: PatternProject;
  selectedColor: number | null;
  focusMode: FocusMode;
  tool: CanvasTool;
  gridMode: GridMode;
  showCodes: boolean;
  showMarks: boolean;
  showHighlight: boolean;
  editDimPercent: number;
  editMode: boolean;
  canUndo: boolean;
  canRedo: boolean;
  selection: CanvasSelection | null;
  mouseButtons: MouseButtonMap;
  hoverCell: { col: number; row: number } | null;
  clipboardSize: { width: number; height: number } | null;
  pastePlacement: boolean;
  tabletMode: boolean;
  touchDevice: boolean;
  beadBoardMode: boolean;
  eyeComfortMode: boolean;
  watermark: PatternWatermark | null;
  onWatermarkChange: (value: PatternWatermark) => void;
  onBeadBoardModeChange: (active: boolean) => void;
  onToolChange: (tool: CanvasTool) => void;
  onToggleComplete: (cellIndex: number, completed?: boolean) => void;
  onEditStart: () => void;
  onEditCell: (cellIndex: number, mode: 'pencil' | 'eraser') => void;
  onFillCell: (cellIndex: number) => void;
  onEditEnd: () => void;
  onPickColor: (colorIndex: number) => void;
  onOpenPalette: () => void;
  onOpenShortcutPalette: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onSelectionChange: (selection: CanvasSelection | null) => void;
  onHoverCellChange: (cell: { col: number; row: number } | null) => void;
  onPasteAt: (cell: { col: number; row: number }) => void;
  onMoveSelection: (selection: CanvasSelection, deltaCol: number, deltaRow: number) => void;
  onCancelSelection: () => void;
  onOpenMouseSettings: () => void;
}

interface ViewState { scale: number; x: number; y: number }

const BEAD_BOARD_CELL_MM = 2.6;
const CSS_PIXELS_PER_MM = 96 / 25.4;

function colorLuminance(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  return (r * 299 + g * 587 + b * 114) / 255000;
}

export function BeadCanvas({ project, selectedColor, focusMode, gridMode, tool, showCodes, showMarks, showHighlight, editDimPercent, editMode, canUndo, canRedo, selection, mouseButtons, hoverCell, clipboardSize, pastePlacement, tabletMode, touchDevice, beadBoardMode, eyeComfortMode, watermark, onWatermarkChange, onBeadBoardModeChange, onToolChange, onToggleComplete, onEditStart, onEditCell, onFillCell, onEditEnd, onPickColor, onOpenPalette, onOpenShortcutPalette, onUndo, onRedo, onSelectionChange, onHoverCellChange, onPasteAt, onMoveSelection, onCancelSelection, onOpenMouseSettings }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const layerRef = useRef<HTMLCanvasElement | null>(null);
  const layerKeyRef = useRef('');
  const pointers = useRef(new Map<number, { x: number; y: number; pointerType: string }>());
  const lastPenInputAt = useRef(0);
  const dragOrigin = useRef<{ x: number; y: number; viewX: number; viewY: number } | null>(null);
  const pinchDistance = useRef(0);
  const pinchCenter = useRef<{ x: number; y: number } | null>(null);
  const touchGestureLocked = useRef(false);
  const watermarkDrag = useRef<{ pointerId: number; mode: 'move' | 'resize'; offsetCol: number; offsetRow: number } | null>(null);
  const touchEditPending = useRef<{ pointerId: number; point: { x: number; y: number }; action: 'pencil' | 'eraser' } | null>(null);
  const editStrokeActive = useRef(false);
  const lastEditIndex = useRef(-1);
  const markStrokeValue = useRef<boolean | null>(null);
  const lastMarkCell = useRef<{ col: number; row: number } | null>(null);
  const markedInStroke = useRef(new Set<number>());
  const gestureAction = useRef<CanvasTool | null>(null);
  const selectionOrigin = useRef<{ col: number; row: number } | null>(null);
  const selectionMoveOrigin = useRef<{ col: number; row: number; selection: CanvasSelection; deltaCol: number; deltaRow: number } | null>(null);
  const lastHoverPoint = useRef<{ x: number; y: number } | null>(null);
  const reportedHoverKey = useRef('');
  const lastHostSize = useRef<{ width: number; height: number } | null>(null);
  const [size, setSize] = useState({ width: 800, height: 600, dpr: 1 });
  const [view, setView] = useState<ViewState>({ scale: 8, x: 80, y: 80 });
  const [background, setBackground] = useState<HTMLImageElement | null>(null);
  const [stylusSeen, setStylusSeen] = useState(false);
  const [stylusPressure, setStylusPressure] = useState(0);
  const [touchMode, setTouchMode] = useState(() => window.localStorage.getItem('yxmpd-touch-mode-v1') === 'draw');
  const [runAssistMode, setRunAssistMode] = useState<RunAssistMode>('none');
  const [boardCalibration, setBoardCalibration] = useState(() => {
    const stored = Number(window.localStorage.getItem('yxmpd-board-calibration-v1'));
    return Number.isFinite(stored) && stored >= 0.7 && stored <= 1.5 ? stored : 1;
  });
  const colorRuns = useMemo(() => selectedColor === null || runAssistMode === 'none' || editMode
    ? []
    : findColorRuns(project, selectedColor, runAssistMode), [editMode, project, runAssistMode, selectedColor]);
  const runAssistedCells = useMemo(() => {
    const cells = new Set<number>();
    for (const run of colorRuns) {
      for (let offset = 0; offset < run.length; offset += 1) {
        const col = run.col + (run.orientation === 'horizontal' ? offset : 0);
        const row = run.row + (run.orientation === 'vertical' ? offset : 0);
        cells.add(row * project.width + col);
      }
    }
    return cells;
  }, [colorRuns, project.width]);

  useEffect(() => {
    window.localStorage.setItem('yxmpd-touch-mode-v1', touchMode ? 'draw' : 'navigate');
  }, [touchMode]);

  useEffect(() => {
    const handleRunAssist = (event: Event) => setRunAssistMode((event as CustomEvent<RunAssistMode>).detail || 'none');
    window.addEventListener('yxmpd-run-assist-change', handleRunAssist);
    return () => window.removeEventListener('yxmpd-run-assist-change', handleRunAssist);
  }, []);

  useEffect(() => {
    window.localStorage.setItem('yxmpd-board-calibration-v1', boardCalibration.toFixed(3));
  }, [boardCalibration]);

  useEffect(() => {
    if (touchDevice && touchMode && tool === 'pan') onToolChange(editMode ? 'pencil' : 'mark');
  }, [touchDevice, touchMode]);

  useEffect(() => {
    if (lastHoverPoint.current) reportHoverAt(lastHoverPoint.current);
  }, [onHoverCellChange, project.height, project.width, view]);

  useEffect(() => {
    if (!project.backgroundImage) {
      setBackground(null);
      return;
    }
    const image = new Image();
    image.onload = () => setBackground(image);
    image.src = project.backgroundImage;
    return () => { image.onload = null; };
  }, [project.backgroundImage]);

  const fitView = useCallback(() => {
    const rect = hostRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pad = rect.width < 600 ? 38 : 64;
    const availableWidth = Math.max(100, rect.width - pad * 2);
    const availableHeight = Math.max(100, rect.height - pad * 2);
    const scale = beadBoardMode
      ? BEAD_BOARD_CELL_MM * CSS_PIXELS_PER_MM * boardCalibration
      : Math.max(0.75, Math.min(28, availableWidth / project.width, availableHeight / project.height));
    setView({
      scale,
      x: (rect.width - project.width * scale) / 2,
      y: (rect.height - project.height * scale) / 2
    });
  }, [beadBoardMode, boardCalibration, project.height, project.width]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const update = () => {
      const rect = host.getBoundingClientRect();
      const previous = lastHostSize.current;
      lastHostSize.current = { width: rect.width, height: rect.height };
      setSize({ width: rect.width, height: rect.height, dpr: Math.min(2, window.devicePixelRatio || 1) });
      if (previous) {
        const deltaWidth = rect.width - previous.width;
        const deltaHeight = rect.height - previous.height;
        if (Math.abs(deltaWidth) > 1 || Math.abs(deltaHeight) > 1) {
          setView((current) => ({
            ...current,
            x: current.x + deltaWidth / 2,
            y: current.y + deltaHeight / 2
          }));
        }
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(host);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    layerKeyRef.current = '';
    requestAnimationFrame(fitView);
  }, [fitView, project.id]);

  const marksVisible = !editMode && showMarks;
  const editDimActive = editMode && editDimPercent > 0 && selectedColor !== null;
  const editDimOpacity = 1 - editDimPercent / 100;
  const layerKey = useMemo(() => `${project.id}:${project.updatedAt}:${selectedColor ?? 'none'}:${focusMode}:${marksVisible ? 'marks' : 'plain'}:${editDimActive ? `edit-dim-${editDimPercent}` : 'edit-full'}`, [editDimActive, editDimPercent, focusMode, marksVisible, project.id, project.updatedAt, selectedColor]);

  const highlightOnDark = useMemo(() => {
    if (!editMode || !showHighlight || selectedColor === null) return false;
    const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]] as const;
    let darkCells = 0;
    let brightCells = 0;
    for (let row = 0; row < project.height; row += 1) {
      for (let col = 0; col < project.width; col += 1) {
        const index = row * project.width + col;
        if (project.empty[index] || project.colorIndices[index] !== selectedColor) continue;
        let neighborLuminance = 0;
        let neighborCount = 0;
        for (const [deltaCol, deltaRow] of directions) {
          const nextCol = col + deltaCol;
          const nextRow = row + deltaRow;
          if (nextCol < 0 || nextCol >= project.width || nextRow < 0 || nextRow >= project.height) continue;
          const neighborIndex = nextRow * project.width + nextCol;
          neighborLuminance += project.empty[neighborIndex] ? 1 : colorLuminance(MARD_291[project.colorIndices[neighborIndex]]?.hex || '#ffffff');
          neighborCount += 1;
        }
        const surrounding = neighborCount
          ? neighborLuminance / neighborCount
          : colorLuminance(MARD_291[selectedColor]?.hex || '#ffffff');
        if (surrounding < 0.48) darkCells += 1;
        else brightCells += 1;
      }
    }
    return darkCells > brightCells;
  }, [editMode, project.colorIndices, project.empty, project.height, project.width, selectedColor, showHighlight]);

  const buildLayer = useCallback(() => {
    if (layerRef.current && layerKeyRef.current === layerKey) return layerRef.current;
    const layer = layerRef.current || document.createElement('canvas');
    layer.width = project.width;
    layer.height = project.height;
    const context = layer.getContext('2d');
    if (!context) return layer;
    const image = context.createImageData(project.width, project.height);
    for (let i = 0; i < project.colorIndices.length; i += 1) {
      const pixel = i * 4;
      if (project.empty[i]) {
        image.data[pixel + 3] = 0;
        continue;
      }
      const colorIndex = project.colorIndices[i];
      const rgb = hexToRgb(MARD_291[colorIndex]?.hex || '#ffffff');
      const matches = selectedColor === null || colorIndex === selectedColor;
      let alpha = 1;
      if (!matches && editDimActive) alpha = editDimOpacity;
      if (!editMode && !matches && focusMode === 'dim') alpha = 0.14;
      if (!editMode && !matches && focusMode === 'solo') alpha = 0;
      if (marksVisible && project.completed[i]) alpha *= 0.38;
      image.data[pixel] = Math.round(rgb.r * alpha + 255 * (1 - alpha));
      image.data[pixel + 1] = Math.round(rgb.g * alpha + 255 * (1 - alpha));
      image.data[pixel + 2] = Math.round(rgb.b * alpha + 255 * (1 - alpha));
      image.data[pixel + 3] = 255;
    }
    context.putImageData(image, 0, 0);
    layerRef.current = layer;
    layerKeyRef.current = layerKey;
    return layer;
  }, [editDimActive, editDimOpacity, editMode, focusMode, layerKey, marksVisible, project, selectedColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = Math.max(1, Math.round(size.width * size.dpr));
    canvas.height = Math.max(1, Math.round(size.height * size.dpr));
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(size.dpr, 0, 0, size.dpr, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    const boardWidth = project.width * view.scale;
    const boardHeight = project.height * view.scale;
    ctx.save();
    ctx.shadowColor = 'rgba(35, 29, 24, 0.12)';
    ctx.shadowBlur = 26;
    ctx.shadowOffsetY = 12;
    ctx.fillStyle = eyeComfortMode ? '#f7efdc' : '#ffffff';
    ctx.fillRect(view.x, view.y, boardWidth, boardHeight);
    ctx.restore();

    if (background) {
      const scale = Math.min(boardWidth / background.naturalWidth, boardHeight / background.naturalHeight);
      const drawWidth = background.naturalWidth * scale;
      const drawHeight = background.naturalHeight * scale;
      ctx.save();
      ctx.globalAlpha = project.backgroundOpacity ?? 0.35;
      if (project.backgroundMirror) {
        ctx.translate(view.x + boardWidth, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(background, (boardWidth - drawWidth) / 2, view.y + (boardHeight - drawHeight) / 2, drawWidth, drawHeight);
      } else {
        ctx.drawImage(background, view.x + (boardWidth - drawWidth) / 2, view.y + (boardHeight - drawHeight) / 2, drawWidth, drawHeight);
      }
      ctx.restore();
    }

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(buildLayer(), view.x, view.y, boardWidth, boardHeight);

    if (watermark) {
      ctx.save();
      ctx.strokeStyle = 'rgba(23, 32, 51, 0.34)';
      ctx.lineWidth = Math.max(0.6, view.scale * 0.04);
      watermarkCells(watermark).forEach((cell) => {
        const x = view.x + cell.col * view.scale;
        const y = view.y + cell.row * view.scale;
        const size = cell.size * view.scale;
        ctx.fillStyle = MARD_291[cell.colorIndex]?.hex || '#172033';
        ctx.fillRect(x, y, size, size);
        ctx.strokeRect(x, y, size, size);
      });
      if (editMode && !watermark.locked) {
        const scale = Math.max(0.25, Math.min(4, Number(watermark.scale) || 1));
        const watermarkWidth = watermark.width * scale;
        const watermarkHeight = watermark.height * scale;
        ctx.setLineDash([Math.max(5, view.scale * 0.35), Math.max(4, view.scale * 0.22)]);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(124,92,255,.9)';
        ctx.strokeRect(view.x + watermark.x * view.scale - 3, view.y + watermark.y * view.scale - 3, watermarkWidth * view.scale + 6, watermarkHeight * view.scale + 6);
        ctx.setLineDash([]);
        ctx.fillStyle = '#6d50dc';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        const handleX = view.x + (watermark.x + watermarkWidth) * view.scale;
        const handleY = view.y + (watermark.y + watermarkHeight) * view.scale;
        ctx.fillRect(handleX - 7, handleY - 7, 14, 14);
        ctx.strokeRect(handleX - 7, handleY - 7, 14, 14);
      }
      ctx.restore();
    }

    const minCol = Math.max(0, Math.floor(-view.x / view.scale) - 1);
    const maxCol = Math.min(project.width, Math.ceil((size.width - view.x) / view.scale) + 1);
    const minRow = Math.max(0, Math.floor(-view.y / view.scale) - 1);
    const maxRow = Math.min(project.height, Math.ceil((size.height - view.y) / view.scale) + 1);

    if (gridMode !== 'none' && view.scale >= 3) {
      ctx.save();
      ctx.beginPath();
      ctx.globalCompositeOperation = 'difference';
      ctx.strokeStyle = view.scale >= 9 ? 'rgba(255, 255, 255, 0.32)' : 'rgba(255, 255, 255, 0.24)';
      ctx.lineWidth = 1;
      for (let col = minCol; col <= maxCol; col += 1) {
        if (gridMode !== 'fine' && col % 5 === 0) continue;
        const x = Math.round(view.x + col * view.scale) + 0.5;
        ctx.moveTo(x, view.y + minRow * view.scale);
        ctx.lineTo(x, view.y + maxRow * view.scale);
      }
      for (let row = minRow; row <= maxRow; row += 1) {
        if (gridMode !== 'fine' && row % 5 === 0) continue;
        const y = Math.round(view.y + row * view.scale) + 0.5;
        ctx.moveTo(view.x + minCol * view.scale, y);
        ctx.lineTo(view.x + maxCol * view.scale, y);
      }
      ctx.stroke();
      ctx.restore();
    }

    if (gridMode === 'guides' || gridMode === 'blocks5') {
      ctx.save();
      ctx.beginPath();
      if (gridMode === 'blocks5') ctx.setLineDash([]);
      else {
        ctx.setLineDash([Math.max(3, view.scale * 0.12), Math.max(4, view.scale * 0.18)]);
        ctx.lineCap = 'round';
      }
      ctx.strokeStyle = gridMode === 'blocks5' ? 'rgba(226, 75, 63, 0.68)' : 'rgba(255, 96, 86, 0.76)';
      ctx.lineWidth = gridMode === 'blocks5' ? (view.scale >= 8 ? 1.15 : 0.8) : (view.scale >= 8 ? 1.15 : 0.85);
      for (let col = 5; col < project.width; col += 10) {
        const x = Math.round(view.x + col * view.scale) + 0.5;
        ctx.moveTo(x, view.y);
        ctx.lineTo(x, view.y + boardHeight);
      }
      for (let row = 5; row < project.height; row += 10) {
        const y = Math.round(view.y + row * view.scale) + 0.5;
        ctx.moveTo(view.x, y);
        ctx.lineTo(view.x + boardWidth, y);
      }
      ctx.stroke();
      ctx.restore();
    }

    if (gridMode !== 'none') {
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(226, 75, 63, 0.72)';
      ctx.lineWidth = gridMode === 'blocks5' ? (view.scale >= 8 ? 1.45 : 1) : (view.scale >= 8 ? 1.35 : 0.95);
      for (let col = 10; col < project.width; col += 10) {
        const x = Math.round(view.x + col * view.scale) + 0.5;
        ctx.moveTo(x, view.y);
        ctx.lineTo(x, view.y + boardHeight);
      }
      for (let row = 10; row < project.height; row += 10) {
        const y = Math.round(view.y + row * view.scale) + 0.5;
        ctx.moveTo(view.x, y);
        ctx.lineTo(view.x + boardWidth, y);
      }
      ctx.stroke();
      ctx.strokeStyle = 'rgba(24, 21, 19, 0.82)';
      ctx.lineWidth = 2;
      ctx.strokeRect(view.x, view.y, boardWidth, boardHeight);
      ctx.restore();
    }

    if (editMode && showHighlight && selectedColor !== null) {
      ctx.save();
      const inset = Math.min(1.25, Math.max(0.3, view.scale * 0.045));
      const outerWidth = Math.min(4.4, Math.max(0.9, view.scale * 0.15));
      const innerWidth = Math.min(1.8, Math.max(0.55, view.scale * 0.06));
      for (let row = minRow; row < maxRow; row += 1) {
        for (let col = minCol; col < maxCol; col += 1) {
          const index = row * project.width + col;
          if (project.empty[index] || project.colorIndices[index] !== selectedColor) continue;
          const x = view.x + col * view.scale + inset;
          const y = view.y + row * view.scale + inset;
          const cellSize = Math.max(0.1, view.scale - inset * 2);
          ctx.strokeStyle = highlightOnDark ? 'rgba(255, 241, 116, 0.98)' : 'rgba(12, 35, 62, 0.98)';
          ctx.lineWidth = outerWidth;
          ctx.strokeRect(x, y, cellSize, cellSize);
          ctx.strokeStyle = highlightOnDark ? 'rgba(255, 255, 255, 0.98)' : 'rgba(0, 198, 255, 0.98)';
          ctx.lineWidth = innerWidth;
          ctx.strokeRect(x, y, cellSize, cellSize);
        }
      }
      ctx.restore();
    }

    const codeScaleThreshold = focusMode === 'all' ? 9.5 : 7.5;
    if (showCodes && view.scale >= codeScaleThreshold) {
      const fontSize = Math.max(8, Math.min(17, view.scale * 0.52));
      ctx.font = `600 ${fontSize}px ui-monospace, SFMono-Regular, Consolas, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (let row = minRow; row < maxRow; row += 1) {
        for (let col = minCol; col < maxCol; col += 1) {
          const index = row * project.width + col;
          if (project.empty[index]) continue;
          if (runAssistedCells.has(index)) continue;
          const colorIndex = project.colorIndices[index];
          if (selectedColor !== null && focusMode !== 'all' && colorIndex !== selectedColor) continue;
          const color = MARD_291[colorIndex];
          if (!color) continue;
          const dimmedCode = editDimActive && colorIndex !== selectedColor;
          if (dimmedCode) {
            const rgb = hexToRgb(color.hex);
            const dimmedCodeLuminance = (
              (rgb.r * editDimOpacity + 255 * (1 - editDimOpacity)) * 299
              + (rgb.g * editDimOpacity + 255 * (1 - editDimOpacity)) * 587
              + (rgb.b * editDimOpacity + 255 * (1 - editDimOpacity)) * 114
            ) / 1000;
            ctx.globalAlpha = 1;
            ctx.fillStyle = dimmedCodeLuminance > 128 ? '#252a31' : '#fffaf2';
          } else {
            ctx.globalAlpha = 1;
            ctx.fillStyle = readableTextColor(color.hex) === '#ffffff' ? '#eee9df' : '#25272d';
          }
          const displayCode = project.paletteCodeMap?.[String(colorIndex)] || color.code;
          ctx.fillText(displayCode, view.x + (col + 0.5) * view.scale, view.y + (row + 0.5) * view.scale, Math.max(1, view.scale - 1.5));
        }
      }
      ctx.globalAlpha = 1;
    }

    if (colorRuns.length) {
      ctx.save();
      for (const run of colorRuns) {
        const horizontal = run.orientation === 'horizontal';
        const width = (horizontal ? run.length : 1) * view.scale;
        const height = (horizontal ? 1 : run.length) * view.scale;
        const x = view.x + run.col * view.scale;
        const y = view.y + run.row * view.scale;

        ctx.strokeStyle = 'rgba(83, 54, 25, .94)';
        ctx.lineWidth = Math.max(1, Math.min(2, view.scale * .08));
        ctx.setLineDash([Math.max(3, view.scale * .24), Math.max(2, view.scale * .14)]);
        ctx.strokeRect(x + .5, y + .5, Math.max(1, width - 1), Math.max(1, height - 1));
        ctx.setLineDash([]);

        ctx.strokeStyle = 'rgba(109, 72, 32, .62)';
        ctx.lineWidth = 1;
        for (let offset = 1; offset < run.length; offset += 1) {
          ctx.beginPath();
          if (horizontal) {
            const separatorX = x + offset * view.scale;
            ctx.moveTo(separatorX, y + 1);
            ctx.lineTo(separatorX, y + height - 1);
          } else {
            const separatorY = y + offset * view.scale;
            ctx.moveTo(x + 1, separatorY);
            ctx.lineTo(x + width - 1, separatorY);
          }
          ctx.stroke();
        }

        if (view.scale < 10) continue;
        const fontSize = Math.max(10, Math.min(14, view.scale * .5));
        ctx.font = `800 ${fontSize}px ui-monospace, SFMono-Regular, Consolas, monospace`;
        const runColor = selectedColor === null ? null : MARD_291[selectedColor];
        ctx.fillStyle = runColor && readableTextColor(runColor.hex) === '#ffffff' ? '#fffaf2' : '#25272d';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (let offset = 0; offset < run.length; offset += 1) {
          const centerX = x + (horizontal ? offset + .5 : .5) * view.scale;
          const centerY = y + (horizontal ? .5 : offset + .5) * view.scale;
          ctx.fillText(String(offset + 1), centerX, centerY + .5, Math.max(1, view.scale - 2));
        }
      }
      ctx.restore();
    }

    if (marksVisible && view.scale >= 10) {
      ctx.strokeStyle = 'rgba(25, 23, 21, 0.72)';
      ctx.lineWidth = Math.max(1.4, view.scale * 0.08);
      for (let row = minRow; row < maxRow; row += 1) {
        for (let col = minCol; col < maxCol; col += 1) {
          const index = row * project.width + col;
          if (!project.completed[index] || project.empty[index]) continue;
          const cx = view.x + (col + 0.5) * view.scale;
          const cy = view.y + (row + 0.5) * view.scale;
          const r = view.scale * 0.23;
          ctx.beginPath();
          ctx.moveTo(cx - r, cy);
          ctx.lineTo(cx - r * 0.2, cy + r * 0.7);
          ctx.lineTo(cx + r, cy - r * 0.8);
          ctx.stroke();
        }
      }
    }

    if (selection) {
      const left = Math.min(selection.startCol, selection.endCol);
      const right = Math.max(selection.startCol, selection.endCol);
      const top = Math.min(selection.startRow, selection.endRow);
      const bottom = Math.max(selection.startRow, selection.endRow);
      const x = view.x + left * view.scale;
      const y = view.y + top * view.scale;
      const width = (right - left + 1) * view.scale;
      const height = (bottom - top + 1) * view.scale;
      ctx.save();
      ctx.fillStyle = 'rgba(124, 92, 255, 0.12)';
      ctx.fillRect(x, y, width, height);
      ctx.strokeStyle = '#6d50dc';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(x + 1, y + 1, Math.max(0, width - 2), Math.max(0, height - 2));
      ctx.setLineDash([]);
      ctx.fillStyle = '#6d50dc';
      [[x, y], [x + width, y], [x, y + height], [x + width, y + height]].forEach(([handleX, handleY]) => ctx.fillRect(handleX - 3, handleY - 3, 6, 6));
      ctx.restore();
    }

    if (pastePlacement && clipboardSize && hoverCell) {
      const width = Math.min(clipboardSize.width, project.width - hoverCell.col) * view.scale;
      const height = Math.min(clipboardSize.height, project.height - hoverCell.row) * view.scale;
      const x = view.x + hoverCell.col * view.scale;
      const y = view.y + hoverCell.row * view.scale;
      ctx.save();
      ctx.fillStyle = 'rgba(246, 101, 91, 0.1)';
      ctx.fillRect(x, y, width, height);
      ctx.strokeStyle = '#e4574e';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(x + 1, y + 1, Math.max(0, width - 2), Math.max(0, height - 2));
      ctx.restore();
    }

    if (view.scale >= 4.5) {
      const rulerSize = 24;
      const boardRight = view.x + boardWidth;
      const boardBottom = view.y + boardHeight;
      const visibleLeft = Math.max(0, view.x);
      const visibleRight = Math.min(size.width, boardRight);
      const visibleTop = Math.max(0, view.y);
      const visibleBottom = Math.min(size.height, boardBottom);
      if (visibleRight > visibleLeft && visibleBottom > visibleTop) {
        const topY = Math.max(0, Math.min(size.height - rulerSize, view.y - rulerSize));
        const bottomY = Math.max(0, Math.min(size.height - rulerSize, boardBottom));
        const leftX = Math.max(0, Math.min(size.width - rulerSize, view.x - rulerSize));
        const rightX = Math.max(0, Math.min(size.width - rulerSize, boardRight));
        const labelSize = Math.min(13, Math.max(10, view.scale * 0.46));
        const step = view.scale >= 24 ? 1 : view.scale >= 12 ? 2 : view.scale >= 7 ? 5 : 10;
        const firstColumn = Math.max(1, Math.floor((visibleLeft - view.x) / view.scale) + 1);
        const lastColumn = Math.min(project.width, Math.ceil((visibleRight - view.x) / view.scale));
        const firstRow = Math.max(1, Math.floor((visibleTop - view.y) / view.scale) + 1);
        const lastRow = Math.min(project.height, Math.ceil((visibleBottom - view.y) / view.scale));
        const valuesInView = (start: number, end: number) => {
          const values: number[] = [];
          for (let value = start; value <= end; value += 1) {
            if (value === 1 || value === end || value % step === 0) values.push(value);
          }
          return values;
        };
        const columnValues = valuesInView(firstColumn, lastColumn);
        const rowValues = valuesInView(firstRow, lastRow);
        ctx.save();
        ctx.fillStyle = eyeComfortMode ? 'rgba(250, 243, 225, .94)' : 'rgba(255, 255, 255, .94)';
        ctx.strokeStyle = 'rgba(92, 100, 116, .28)';
        ctx.lineWidth = 1;
        ctx.shadowColor = 'rgba(30, 38, 53, .12)';
        ctx.shadowBlur = 8;
        ctx.fillRect(visibleLeft, topY, visibleRight - visibleLeft, rulerSize);
        ctx.fillRect(visibleLeft, bottomY, visibleRight - visibleLeft, rulerSize);
        ctx.fillRect(leftX, visibleTop, rulerSize, visibleBottom - visibleTop);
        ctx.fillRect(rightX, visibleTop, rulerSize, visibleBottom - visibleTop);
        ctx.shadowBlur = 0;
        ctx.strokeRect(visibleLeft, topY, visibleRight - visibleLeft, rulerSize);
        ctx.strokeRect(visibleLeft, bottomY, visibleRight - visibleLeft, rulerSize);
        ctx.strokeRect(leftX, visibleTop, rulerSize, visibleBottom - visibleTop);
        ctx.strokeRect(rightX, visibleTop, rulerSize, visibleBottom - visibleTop);
        ctx.fillStyle = '#4b5362';
        ctx.font = `750 ${labelSize}px ui-monospace, SFMono-Regular, Consolas, monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (const column of columnValues) {
          const x = view.x + (column - 0.5) * view.scale;
          if (x < visibleLeft + 7 || x > visibleRight - 7) continue;
          ctx.fillText(String(column), x, topY + rulerSize / 2);
          ctx.fillText(String(column), x, bottomY + rulerSize / 2);
        }
        for (const row of rowValues) {
          const y = view.y + (row - 0.5) * view.scale;
          if (y < visibleTop + 7 || y > visibleBottom - 7) continue;
          ctx.fillText(String(row), leftX + rulerSize / 2, y);
          ctx.fillText(String(row), rightX + rulerSize / 2, y);
        }
        ctx.restore();
      }
    }
  }, [background, buildLayer, clipboardSize, colorRuns, editDimActive, editDimOpacity, editMode, eyeComfortMode, focusMode, gridMode, highlightOnDark, hoverCell, marksVisible, pastePlacement, project, runAssistedCells, selectedColor, selection, showCodes, showHighlight, size, tool, view, watermark]);

  const zoomAt = useCallback((factor: number, x: number, y: number) => {
    if (beadBoardMode) onBeadBoardModeChange(false);
    setView((current) => {
      const scale = Math.max(0.5, Math.min(64, current.scale * factor));
      const ratio = scale / current.scale;
      return { scale, x: x - (x - current.x) * ratio, y: y - (y - current.y) * ratio };
    });
  }, [beadBoardMode, onBeadBoardModeChange]);

  useEffect(() => {
    const handleZoomShortcut = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || event.ctrlKey || event.metaKey || event.altKey || document.querySelector('[role="dialog"]')) return;
      const target = event.target as HTMLElement | null;
      if (target?.matches('input, textarea, select, [contenteditable="true"]')) return;
      if (event.key === '-') zoomAt(0.82, size.width / 2, size.height / 2);
      else if (event.key === '0') fitView();
      else if (event.key === '+' || event.key === '=') zoomAt(1.22, size.width / 2, size.height / 2);
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', handleZoomShortcut);
    return () => window.removeEventListener('keydown', handleZoomShortcut);
  }, [fitView, size.height, size.width, zoomAt]);

  function localPoint(event: React.PointerEvent) {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function smartAlternate(current: CanvasTool): CanvasTool {
    if (current === 'pencil') return 'eraser';
    if (current === 'fill') return 'eraser';
    if (current === 'eraser') return 'pencil';
    if (current === 'picker') return 'pencil';
    if (current === 'select') return 'eraser';
    if (current === 'pan') return 'mark';
    return 'pan';
  }

  function resolveAction(event: React.PointerEvent): CanvasTool {
    if (event.pointerType === 'pen') {
      if (editMode && (event.button === 5 || (event.buttons & 32) !== 0)) return 'eraser';
      return tool;
    }
    if (event.pointerType === 'touch') {
      return touchDevice && touchMode ? tool : 'pan';
    }
    if (event.pointerType !== 'mouse') return tool;
    const key = event.button === 2 ? 'right' : event.button === 1 ? 'middle' : 'left';
    const configured = mouseButtons[key];
    const configuredLeft = mouseButtons.left;
    const primaryAction = configuredLeft === 'current' || configuredLeft === 'alternate' ? tool : configuredLeft;
    const action = configured === 'current' ? tool : configured === 'alternate' ? smartAlternate(primaryAction) : configured;
    if (!editMode && ['pencil', 'fill', 'eraser', 'picker', 'select'].includes(action)) return tool;
    return action;
  }

  function cellCoordinatesAt(point: { x: number; y: number }) {
    const col = Math.floor((point.x - view.x) / view.scale);
    const row = Math.floor((point.y - view.y) / view.scale);
    if (col < 0 || col >= project.width || row < 0 || row >= project.height) return null;
    return { col, row };
  }

  function reportHoverAt(point: { x: number; y: number }) {
    const cell = cellCoordinatesAt(point);
    const key = cell ? `${cell.col}:${cell.row}` : 'outside';
    if (key === reportedHoverKey.current) return;
    reportedHoverKey.current = key;
    onHoverCellChange(cell);
  }

  function isInsideSelection(cell: { col: number; row: number }, current: CanvasSelection) {
    const left = Math.min(current.startCol, current.endCol);
    const right = Math.max(current.startCol, current.endCol);
    const top = Math.min(current.startRow, current.endRow);
    const bottom = Math.max(current.startRow, current.endRow);
    return cell.col >= left && cell.col <= right && cell.row >= top && cell.row <= bottom;
  }

  function pointerDown(event: React.PointerEvent) {
    event.preventDefault();
    if (event.pointerType === 'touch' && performance.now() - lastPenInputAt.current < 800) return;
    if (event.pointerType === 'pen') {
      lastPenInputAt.current = performance.now();
      setStylusSeen(true);
      setStylusPressure(event.pressure);
      for (const [id, pointer] of pointers.current) if (pointer.pointerType === 'touch') pointers.current.delete(id);
      dragOrigin.current = null;
      pinchDistance.current = 0;
      pinchCenter.current = null;
    }
    (event.currentTarget as HTMLCanvasElement).focus({ preventScroll: true });
    const point = localPoint(event);
    lastHoverPoint.current = point;
    reportHoverAt(point);
    pointers.current.set(event.pointerId, { ...point, pointerType: event.pointerType });
    event.currentTarget.setPointerCapture(event.pointerId);
    if (pointers.current.size === 1) dragOrigin.current = { ...point, viewX: view.x, viewY: view.y };
    if (editMode && watermark && !watermark.locked && pointers.current.size === 1 && event.button === 0) {
      const worldCol = (point.x - view.x) / view.scale;
      const worldRow = (point.y - view.y) / view.scale;
      const scale = Math.max(0.25, Math.min(4, Number(watermark.scale) || 1));
      const right = watermark.x + watermark.width * scale;
      const bottom = watermark.y + watermark.height * scale;
      const handleTolerance = Math.max(0.45, 10 / view.scale);
      if (Math.abs(worldCol - right) <= handleTolerance && Math.abs(worldRow - bottom) <= handleTolerance) {
        watermarkDrag.current = { pointerId: event.pointerId, mode: 'resize', offsetCol: 0, offsetRow: 0 };
        gestureAction.current = null;
        return;
      }
      if (worldCol >= watermark.x && worldCol <= right && worldRow >= watermark.y && worldRow <= bottom) {
        watermarkDrag.current = { pointerId: event.pointerId, mode: 'move', offsetCol: worldCol - watermark.x, offsetRow: worldRow - watermark.y };
        gestureAction.current = null;
        return;
      }
    }
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchDistance.current = Math.hypot(a.x - b.x, a.y - b.y);
      pinchCenter.current = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      if (event.pointerType === 'touch') {
        touchGestureLocked.current = true;
        touchEditPending.current = null;
        if (editStrokeActive.current) {
          onEditEnd();
          editStrokeActive.current = false;
        }
      }
    }
    let action = resolveAction(event);
    if (!editMode && tool === 'mark' && event.pointerType === 'mouse') {
      if (event.button === 0 || event.button === 2) action = 'mark';
      if (event.button === 1) action = 'pan';
    }
    gestureAction.current = action;
    if (pointers.current.size === 1 && pastePlacement && event.button === 0) {
      const cell = cellCoordinatesAt(point);
      if (cell) onPasteAt(cell);
      gestureAction.current = null;
      return;
    }
    if (pointers.current.size === 1 && (action === 'pencil' || action === 'eraser')) {
      if (event.pointerType === 'touch') {
        touchEditPending.current = { pointerId: event.pointerId, point, action };
      } else {
        onEditStart();
        editStrokeActive.current = true;
        lastEditIndex.current = -1;
        editPoint(point, action);
      }
    }
    if (pointers.current.size === 1 && action === 'mark') {
      markStrokeValue.current = event.pointerType === 'mouse' ? event.button !== 2 : true;
      lastMarkCell.current = null;
      markedInStroke.current.clear();
      markPoint(point);
    }
    if (pointers.current.size === 1 && action === 'picker') {
      const index = cellAt(point);
      if (index >= 0 && !project.empty[index]) onPickColor(project.colorIndices[index]);
    }
    if (pointers.current.size === 1 && action === 'select') {
      const cell = cellCoordinatesAt(point);
      if (cell && selection && isInsideSelection(cell, selection)) {
        selectionMoveOrigin.current = { ...cell, selection, deltaCol: 0, deltaRow: 0 };
        selectionOrigin.current = null;
      } else {
        selectionMoveOrigin.current = null;
        selectionOrigin.current = cell;
        if (cell) onSelectionChange({ startCol: cell.col, startRow: cell.row, endCol: cell.col, endRow: cell.row });
      }
    }
  }

  function cellAt(point: { x: number; y: number }) {
    const cell = cellCoordinatesAt(point);
    return cell ? cell.row * project.width + cell.col : -1;
  }

  function editPoint(point: { x: number; y: number }, mode: 'pencil' | 'eraser') {
    const index = cellAt(point);
    if (index < 0 || index === lastEditIndex.current || (mode === 'pencil' && selectedColor === null)) return;
    lastEditIndex.current = index;
    onEditCell(index, mode);
  }

  function markPoint(point: { x: number; y: number }) {
    const cell = cellCoordinatesAt(point);
    if (!cell) return;
    const previous = lastMarkCell.current;
    const steps = previous ? Math.max(Math.abs(cell.col - previous.col), Math.abs(cell.row - previous.row)) : 0;
    const visit = (col: number, row: number) => {
      const index = row * project.width + col;
      if (project.empty[index] || markedInStroke.current.has(index)) return;
      if (markStrokeValue.current === null) markStrokeValue.current = true;
      markedInStroke.current.add(index);
      onToggleComplete(index, markStrokeValue.current);
    };
    if (!previous || steps === 0) {
      visit(cell.col, cell.row);
    } else {
      for (let step = 1; step <= steps; step += 1) {
        visit(
          Math.round(previous.col + ((cell.col - previous.col) * step) / steps),
          Math.round(previous.row + ((cell.row - previous.row) * step) / steps)
        );
      }
    }
    lastMarkCell.current = cell;
  }

  function pointerMove(event: React.PointerEvent) {
    if (event.pointerType === 'touch' && performance.now() - lastPenInputAt.current < 800 && !pointers.current.has(event.pointerId)) return;
    if (event.pointerType === 'pen') {
      lastPenInputAt.current = performance.now();
      setStylusSeen(true);
      setStylusPressure(event.pressure);
    }
    const point = localPoint(event);
    lastHoverPoint.current = point;
    reportHoverAt(point);
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { ...point, pointerType: event.pointerType });
    const activeWatermarkDrag = watermarkDrag.current;
    if (activeWatermarkDrag?.pointerId === event.pointerId && pointers.current.size === 1 && watermark) {
      const worldCol = (point.x - view.x) / view.scale;
      const worldRow = (point.y - view.y) / view.scale;
      if (activeWatermarkDrag.mode === 'resize') {
        const widthScale = (worldCol - watermark.x) / Math.max(1, watermark.width);
        const heightScale = (worldRow - watermark.y) / Math.max(1, watermark.height);
        const scale = Math.max(0.25, Math.min(4, Math.round(Math.max(widthScale, heightScale) * 20) / 20));
        onWatermarkChange({ ...watermark, scale, locked: false });
      } else {
        onWatermarkChange({ ...watermark, x: Math.round(worldCol - activeWatermarkDrag.offsetCol), y: Math.round(worldRow - activeWatermarkDrag.offsetRow), locked: false });
      }
      return;
    }
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const previousCenter = pinchCenter.current;
      if (pinchDistance.current > 0 && previousCenter) {
        const factor = distance / pinchDistance.current;
        if (beadBoardMode) onBeadBoardModeChange(false);
        setView((current) => {
          const scale = Math.max(0.5, Math.min(64, current.scale * factor));
          const worldX = (previousCenter.x - current.x) / current.scale;
          const worldY = (previousCenter.y - current.y) / current.scale;
          return { scale, x: center.x - worldX * scale, y: center.y - worldY * scale };
        });
      }
      pinchDistance.current = distance;
      pinchCenter.current = center;
      return;
    }
    if (event.pointerType === 'touch' && touchGestureLocked.current) return;
    const panOrigin = dragOrigin.current;
    const action = gestureAction.current;
    if (action === 'pan' && panOrigin && pointers.current.size === 1) {
      setView((current) => ({ ...current, x: panOrigin.viewX + point.x - panOrigin.x, y: panOrigin.viewY + point.y - panOrigin.y }));
    }
    if (pointers.current.size === 1 && (action === 'pencil' || action === 'eraser')) {
      const pending = touchEditPending.current;
      if (event.pointerType === 'touch' && pending?.pointerId === event.pointerId) {
        if (Math.hypot(point.x - pending.point.x, point.y - pending.point.y) < 4) return;
        onEditStart();
        editStrokeActive.current = true;
        lastEditIndex.current = -1;
        editPoint(pending.point, pending.action);
        touchEditPending.current = null;
      }
      editPoint(point, action);
    }
    if (pointers.current.size === 1 && action === 'mark') markPoint(point);
    if (pointers.current.size === 1 && action === 'select') {
      const col = Math.max(0, Math.min(project.width - 1, Math.floor((point.x - view.x) / view.scale)));
      const row = Math.max(0, Math.min(project.height - 1, Math.floor((point.y - view.y) / view.scale)));
      const move = selectionMoveOrigin.current;
      if (move) {
        const left = Math.min(move.selection.startCol, move.selection.endCol);
        const right = Math.max(move.selection.startCol, move.selection.endCol);
        const top = Math.min(move.selection.startRow, move.selection.endRow);
        const bottom = Math.max(move.selection.startRow, move.selection.endRow);
        const deltaCol = Math.max(-left, Math.min(project.width - 1 - right, col - move.col));
        const deltaRow = Math.max(-top, Math.min(project.height - 1 - bottom, row - move.row));
        move.deltaCol = deltaCol;
        move.deltaRow = deltaRow;
        onSelectionChange({ startCol: left + deltaCol, startRow: top + deltaRow, endCol: right + deltaCol, endRow: bottom + deltaRow });
      } else if (selectionOrigin.current) {
        onSelectionChange({ startCol: selectionOrigin.current.col, startRow: selectionOrigin.current.row, endCol: col, endRow: row });
      }
    }
  }

  function pointerUp(event: React.PointerEvent) {
    if (!pointers.current.has(event.pointerId)) return;
    if (event.pointerType === 'pen') {
      lastPenInputAt.current = performance.now();
      setStylusPressure(0);
    }
    const point = localPoint(event);
    const origin = dragOrigin.current;
    const wasTap = origin && Math.hypot(point.x - origin.x, point.y - origin.y) < 8;
    const action = gestureAction.current;
    const pendingTouchEdit = touchEditPending.current;
    if ((action === 'pencil' || action === 'eraser') && pendingTouchEdit?.pointerId === event.pointerId && !touchGestureLocked.current) {
      onEditStart();
      editStrokeActive.current = true;
      lastEditIndex.current = -1;
      editPoint(point, pendingTouchEdit.action);
    }
    if (action === 'fill' && pointers.current.size === 1 && wasTap && !touchGestureLocked.current && selectedColor !== null) {
      const index = cellAt(point);
      if (index >= 0 && (project.empty[index] || project.colorIndices[index] !== selectedColor)) {
        onEditStart();
        onFillCell(index);
      }
    }
    const move = selectionMoveOrigin.current;
    if (action === 'select' && move && (move.deltaCol || move.deltaRow)) onMoveSelection(move.selection, move.deltaCol, move.deltaRow);
    pointers.current.delete(event.pointerId);
    if (watermarkDrag.current?.pointerId === event.pointerId) watermarkDrag.current = null;
    if ((action === 'pencil' || action === 'eraser') && editStrokeActive.current) onEditEnd();
    editStrokeActive.current = false;
    touchEditPending.current = null;
    dragOrigin.current = null;
    pinchDistance.current = 0;
    pinchCenter.current = null;
    lastEditIndex.current = -1;
    markStrokeValue.current = null;
    lastMarkCell.current = null;
    markedInStroke.current.clear();
    gestureAction.current = null;
    selectionOrigin.current = null;
    selectionMoveOrigin.current = null;
    if (pointers.current.size === 0) touchGestureLocked.current = false;
  }

  function pointerLeave() {
    if (pointers.current.size) return;
    lastHoverPoint.current = null;
    reportedHoverKey.current = 'outside';
    onHoverCellChange(null);
  }

  const hoverInsideSelection = Boolean(tool === 'select' && selection && hoverCell && isInsideSelection(hoverCell, selection));

  return (
    <div className={`canvas-stage tool-${tool} ${beadBoardMode ? 'bead-board-mode' : ''} ${eyeComfortMode ? 'eye-comfort-mode' : ''}`} ref={hostRef}>
      <canvas
        className={pastePlacement ? 'paste-placement-cursor' : hoverInsideSelection ? 'selection-move-cursor' : ''}
        ref={canvasRef}
        aria-label={`${project.name} 拼豆画布`}
        aria-keyshortcuts="Control+C Control+V Escape"
        tabIndex={0}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
        onPointerLeave={pointerLeave}
        onContextMenu={(event) => event.preventDefault()}
        onWheel={(event) => { event.preventDefault(); const rect = event.currentTarget.getBoundingClientRect(); zoomAt(event.deltaY > 0 ? 0.9 : 1.1, event.clientX - rect.left, event.clientY - rect.top); }}
      />
      {beadBoardMode && <div className="bead-board-calibration" role="group" aria-label="豆板实物尺寸校准">
        <span><Ruler size={17} /><strong>豆板模式</strong><small>每格 2.6mm · 用尺子校准 10 格为 26mm</small></span>
        <div>
          <button type="button" onClick={() => setBoardCalibration((value) => Math.max(0.7, Number((value - 0.005).toFixed(3))))} aria-label="缩小豆板实物尺寸"><Minus size={16} /></button>
          <b>{Math.round(boardCalibration * 100)}%</b>
          <button type="button" onClick={() => setBoardCalibration((value) => Math.min(1.5, Number((value + 0.005).toFixed(3))))} aria-label="放大豆板实物尺寸"><Plus size={16} /></button>
          <button type="button" onClick={() => setBoardCalibration(1)} aria-label="恢复默认豆板尺寸" title="恢复 100%"><RotateCcw size={15} /></button>
        </div>
      </div>}
      {editMode && selectedColor !== null && <button className="edit-current-color" type="button" onClick={onOpenPalette} aria-label={`当前颜色 MARD ${MARD_291[selectedColor]?.code}，点击更换`} title="当前编辑颜色，点击打开色卡">
        <i style={{ background: MARD_291[selectedColor]?.hex }} />
        <span><small>当前颜色</small><strong>MARD {MARD_291[selectedColor]?.code}</strong></span>
        <Palette size={16} />
      </button>}
      <div className="canvas-bottom-dock">
      <div className="canvas-hint"><Focus size={15} /><span>{touchDevice ? (touchMode ? `手指操作已开启 · 单指${tool === 'pan' ? '移动' : tool === 'mark' ? '标记' : tool === 'pencil' ? '画豆' : tool === 'fill' ? '填充' : tool === 'eraser' ? '擦除' : tool === 'picker' ? '吸色' : '框选'} · 双指移动/缩放` : '手指导航模式 · 单指移动 · 双指移动/缩放 · 触控笔使用当前工具') : pastePlacement ? `点击一个格作为粘贴起点 · Esc 取消` : tool === 'pan' ? '单指移动 · 双指缩放' : tool === 'mark' ? '左键标记 · 右键清除 · 按住可连续操作' : tool === 'pencil' ? '左键画笔 · 右键橡皮 · 中键吸色' : tool === 'fill' ? '点按一个格，连续区域会填入当前颜色' : tool === 'eraser' ? '左键橡皮 · 右键画笔 · 中键吸色' : tool === 'select' ? (selection ? `已框选 ${Math.abs(selection.endCol - selection.startCol) + 1} × ${Math.abs(selection.endRow - selection.startRow) + 1} 格 · Ctrl+C 复制 · 拖动移动 · Esc 取消` : `拖动框选 · Ctrl+V 粘贴${clipboardSize ? '' : '（需先复制）'}`) : '点一下图纸里的颜色即可吸取'}</span><b className="canvas-coordinate-readout">{hoverCell ? `X ${hoverCell.col + 1} · Y ${hoverCell.row + 1}` : 'X - · Y -'}</b></div>
      <div className="canvas-toolbox" aria-label="画布工具">
        {touchDevice && <button className={`touch-mode-button ${touchMode ? 'active' : ''}`} type="button" aria-pressed={touchMode} onClick={() => { const next = !touchMode; setTouchMode(next); if (next && tool === 'pan') onToolChange(editMode ? 'pencil' : 'mark'); }} title={touchMode ? '关闭手指操作，恢复手指导航' : '开启手指操作，单指使用当前工具'}><Hand size={18} /><span>{touchMode ? '手指开' : '手指关'}</span></button>}
        <button className={tool === 'pan' ? 'active' : ''} type="button" onClick={() => onToolChange('pan')} title="拖动画布（H）" aria-keyshortcuts="H"><Hand size={18} /><span>移动</span><kbd>H</kbd></button>
        {!editMode && <button className={tool === 'mark' ? 'active' : ''} type="button" onClick={() => onToolChange('mark')} title="标记已拼（M）" aria-keyshortcuts="M"><MousePointer2 size={18} /><span>标记</span><kbd>M</kbd></button>}
        {editMode && <>
          <i />
          <button className={tool === 'pencil' ? 'active' : ''} type="button" onClick={() => onToolChange('pencil')} title="画入当前颜色（B）" aria-keyshortcuts="B"><Pencil size={18} /><span>画笔</span><kbd>B</kbd></button>
          <button className={tool === 'fill' ? 'active' : ''} type="button" onClick={() => onToolChange('fill')} title="填充连续区域（F）" aria-keyshortcuts="F"><PaintBucket size={18} /><span>填充</span><kbd>F</kbd></button>
          <button className={tool === 'eraser' ? 'active' : ''} type="button" onClick={() => onToolChange('eraser')} title="擦除拼豆格（E）" aria-keyshortcuts="E"><Eraser size={18} /><span>橡皮</span><kbd>E</kbd></button>
          <button className={tool === 'picker' ? 'active' : ''} type="button" onClick={() => onToolChange('picker')} title="从图纸吸取颜色（I）" aria-keyshortcuts="I"><Pipette size={18} /><span>吸色</span><kbd>I</kbd></button>
          <button className={tool === 'select' ? 'active' : ''} type="button" onClick={() => tool === 'select' && (selection || pastePlacement) ? onCancelSelection() : onToolChange('select')} title={tool === 'select' && (selection || pastePlacement) ? '取消当前框选（Esc）' : '框选画布区域（S）'} aria-keyshortcuts="S Escape"><ScanLine size={18} /><span>框选</span><kbd>S</kbd></button>
          <button type="button" onClick={onOpenPalette} title="打开快速色卡（P）" aria-label="打开快速色卡" aria-keyshortcuts="P"><Palette size={18} /><span>色卡</span><kbd>P</kbd></button>
          <button type="button" onClick={onOpenShortcutPalette} title="打开快捷色卡组（Q）" aria-label="打开快捷色卡组" aria-keyshortcuts="Q"><Bookmark size={18} /><span>快捷色卡</span><kbd>Q</kbd></button>
          <button type="button" onClick={onUndo} disabled={!canUndo} aria-label="撤销" title="撤销（Z）" aria-keyshortcuts="Z"><Undo2 size={18} /><kbd>Z</kbd></button>
          <button type="button" onClick={onRedo} disabled={!canRedo} aria-label="重做" title="重做（Y）" aria-keyshortcuts="Y"><Redo2 size={18} /><kbd>Y</kbd></button>
          <button className="mouse-settings-button" type="button" onClick={onOpenMouseSettings} aria-label="鼠标按键设置" title="鼠标按键设置（账号同步）"><Settings2 size={17} /></button>
        </>}
        <i />
        <button type="button" onClick={() => zoomAt(0.82, size.width / 2, size.height / 2)} aria-label="缩小" title="缩小（-）" aria-keyshortcuts="-"><ZoomOut size={18} /><kbd>-</kbd></button>
        <button type="button" onClick={fitView} aria-label="适合窗口" title="适合窗口（0）" aria-keyshortcuts="0"><LocateFixed size={18} /><kbd>0</kbd></button>
        <button type="button" onClick={() => zoomAt(1.22, size.width / 2, size.height / 2)} aria-label="放大" title="放大（+）" aria-keyshortcuts="+"><ZoomIn size={18} /><kbd>+</kbd></button>
      </div>
      </div>
      {stylusSeen && <div className="stylus-status" title="编辑模式下，触控笔执行当前工具，手指用于移动和双指缩放"><Pencil size={15} /><span>Apple Pencil / 触控笔</span><b>{stylusPressure > 0 ? `${Math.round(stylusPressure * 100)}%` : '掌触抑制'}</b></div>}
    </div>
  );
}
